import React, { FunctionComponent } from 'react';
import { Paper, SxProps, Theme, Typography } from '@mui/material';

import {
  AbsoluteLine,
  CalculatedLine,
  DivisionLine,
  MemberLine,
  ParallelLine,
  PerpendicularLine,
  PointSlopeLine,
  PointToPointLine,
} from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';

import AbsoluteLineProperties from './AbsoluteLineProperties';
import DivisionLineProperties from './DivisionLineProperties';
import MemberLineProperties from './MemberLineProperties';
import ParallelLineProperties from './ParallelLineProperties';
import PerpendicularLineProperties from './PerpendicularLineProperties';
import PointSlopeLineProperties from './PointSlopeLineProperties';
import PointToPointLineProperties from './PointToPointLineProperties';

interface Props {
  readonly allowMemberLines?: boolean;

  /**
   * If set, the property panel and title will be omitted, and only a PropertyList will be returned.
   */
  readonly compact?: boolean;

  /**
   * The line being edited.  For editing to work properly, it must exist either
   *   on the currently active component design or in insertion state.
   */
  readonly line: CalculatedLine;

  /**
   * Callback that is called after the reference line has been updated.
   */
  readonly afterUpdateLine?: () => void;

  readonly sx?: SxProps<Theme>;

  readonly elevation?: number;
}

export const ReferenceLineProperties: FunctionComponent<Props> = ({
  allowMemberLines,
  compact,
  line,
  afterUpdateLine,
  sx,
  elevation,
}) => {
  let title;
  let properties;
  let helper;

  switch (line.lineType) {
    case 'Absolute':
      title = 'Absolute Line Properties';
      helper = 'This is an absolute line, defined by a base point location and a direction.';
      properties = <AbsoluteLineProperties line={line as AbsoluteLine} afterUpdateLine={afterUpdateLine} />;
      break;

    case 'Parallel':
      title = 'Parallel Line Properties';
      helper =
        'This is a parallel line. Use the tools below to modify what line it is based on and its offset distance.';
      properties = <ParallelLineProperties line={line as ParallelLine} afterUpdateLine={afterUpdateLine} />;
      break;

    case 'PointSlope':
      title = 'Point-Slope Line Properties';
      helper =
        'This is a point-slope line, defined by the intersection of two other lines and an angle from that point.';
      properties = <PointSlopeLineProperties line={line as PointSlopeLine} afterUpdateLine={afterUpdateLine} />;
      break;

    case 'PointToPoint':
      title = 'Point-to-Point Line Properties';
      helper = 'This is a point-to-point line. Use the tools below to modify the two intersections which define it.';
      properties = <PointToPointLineProperties line={line as PointToPointLine} afterUpdateLine={afterUpdateLine} />;
      break;

    case 'Perpendicular':
      title = 'Perpendicular Line Properties';
      helper =
        'This line is perpendicular to a chosen line and passes through an intersection. Use the tools below to modify the intersection and the line you want to be perpendicular to.';
      properties = <PerpendicularLineProperties line={line as PerpendicularLine} afterUpdateLine={afterUpdateLine} />;
      break;

    case 'Division':
      title = 'Division Line Properties';
      helper =
        'This line is located at a certain fraction of the distance between two parallel lines. Use the tools below to select two lines on the truss and set the division factor for the line.';
      properties = <DivisionLineProperties line={line as DivisionLine} afterUpdateLine={afterUpdateLine} />;
      break;

    case 'Member':
      if (!allowMemberLines) return null;

      title = 'Member Line Properties';
      properties = <MemberLineProperties line={line as MemberLine} />;
  }

  return (
    <PropertiesPanel elevation={elevation} title={compact ? undefined : title} sx={sx}>
      {helper && !compact && (
        <Paper elevation={0} sx={{ marginBottom: 1 }}>
          <Typography variant="body1" color="textSecondary" sx={{ margin: 2 }}>
            {helper}
          </Typography>
        </Paper>
      )}

      {properties}
    </PropertiesPanel>
  );
};
